import { Component, Input, OnInit, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../services/auth.service';
import { PlatformContactsService } from '../../services/platform-contacts.service';

/**
 * Superposición de "acceso pausado" del panel: prueba gratuita vencida o
 * plan pagado restringido (TRIAL_EXPIRED / SUBSCRIPTION_RESTRICTED, ver
 * auth.interceptor.ts). Solo la ven negocios CON plan: super_admin y los
 * usuarios de negocios exentos (p. ej. delegados de un super_admin) nunca
 * reciben esos códigos -- el interceptor también los ignora para ellos.
 *
 * - Propietario (legal o delegado): acciones directas para pagar/contratar
 *   (proyectadas desde app.html: [abActions] + <app-suscripcion>).
 * - Empleado: no puede pagar -- se le indica avisar al propietario.
 * - Todos: contactos reales de la plataforma (configurables por super_admin
 *   en "Contactos de la plataforma"), con acciones de un toque.
 */
@Component({
  selector: 'app-access-blocked',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="trial-expired-overlay ab-overlay" role="alertdialog" aria-modal="true" [attr.aria-labelledby]="'ab-title-' + mode">
      <div class="ab-panel">
        <header class="ab-head">
          <span class="ab-icon" [class.is-trial]="mode === 'trial'" aria-hidden="true">
            <i class="bi" [class.bi-hourglass-bottom]="mode === 'trial'" [class.bi-shield-lock]="mode !== 'trial'"></i>
          </span>
          <div class="ab-head-text">
            <span class="ab-eyebrow">Acceso al panel pausado</span>
            <h2 [id]="'ab-title-' + mode">{{ mode === 'trial' ? 'Tu prueba gratuita finalizó' : 'Tu suscripción no está activa' }}</h2>
            <p *ngIf="mode === 'trial'">Elige un plan para seguir usando TuSala. Todo lo que configuraste (espacios, reservas y clientes) sigue guardado y no se elimina.</p>
            <p *ngIf="mode !== 'trial'">El plan de tu negocio venció o fue cancelado y su período de gracia terminó. Renuévalo o contrata otro plan para recuperar el acceso. Tus espacios, reservas y clientes siguen guardados.</p>
          </div>
        </header>

        <div class="ab-body">
          <section class="ab-main" *ngIf="canPay(); else staffView">
            <div class="ab-actions"><ng-content select="[abActions]"></ng-content></div>
            <ng-content></ng-content>
          </section>
          <ng-template #staffView>
            <section class="ab-main ab-staff">
              <i class="bi bi-person-badge" aria-hidden="true"></i>
              <h3>Solo el propietario puede renovar el plan</h3>
              <p>Avísale al propietario del negocio para que renueve o contrate un plan desde su cuenta. En cuanto lo haga, tu acceso vuelve automáticamente.</p>
            </section>
          </ng-template>

          <aside class="ab-contacts" aria-labelledby="ab-contacts-title">
            <h3 id="ab-contacts-title"><i class="bi bi-headset" aria-hidden="true"></i> ¿Necesitas ayuda?</h3>
            <p>El equipo de TuSala puede ayudarte con pagos, planes o tu acceso.</p>
            <a class="ab-contact ab-contact-primary" *ngIf="whatsappUrl() as url" [href]="url" target="_blank" rel="noopener noreferrer">
              <i class="bi bi-whatsapp" aria-hidden="true"></i>
              <span><small>WhatsApp</small>{{ primaryPhone() }}</span>
            </a>
            <a class="ab-contact" [href]="'mailto:' + supportEmail() + '?subject=' + mailSubject">
              <i class="bi bi-envelope" aria-hidden="true"></i>
              <span><small>Soporte</small>{{ supportEmail() }}</span>
            </a>
            <a class="ab-contact" *ngIf="adminEmail() as email" [href]="'mailto:' + email + '?subject=' + mailSubject">
              <i class="bi bi-briefcase" aria-hidden="true"></i>
              <span><small>Administración y pagos</small>{{ email }}</span>
            </a>
            <a class="ab-contact" *ngFor="let phone of otherPhones()" [href]="'tel:' + phone">
              <i class="bi bi-telephone" aria-hidden="true"></i>
              <span><small>Teléfono</small>{{ phone }}</span>
            </a>
          </aside>
        </div>
      </div>
    </div>
  `,
  styles: [`
    :host { display: contents; }
    /* Posición propia: los estilos de app.css están encapsulados al componente App. */
    .ab-overlay { position: fixed; inset: 0; z-index: 10050; display: grid; place-items: center; padding: 1.25rem; overflow-y: auto; background: var(--ts-bg-navy-950-a82); backdrop-filter: blur(3px); }
    @media print { .ab-overlay { display: none !important; } }
    .ab-panel {
      width: min(1080px, 100%);
      max-height: min(94vh, 920px);
      overflow-y: auto;
      margin: auto;
      border-radius: 20px;
      background: var(--ts-bg-elevated, #0f2743);
      border: 1px solid var(--ts-border, rgba(103,154,236,.2));
      box-shadow: 0 30px 90px var(--ts-shadow-gray-1000-a45);
    }
    .ab-head { display: flex; align-items: flex-start; gap: 1rem; padding: 1.5rem 1.6rem 1.25rem; border-bottom: 1px solid var(--ts-border, rgba(103,154,236,.2)); }
    .ab-icon { flex: 0 0 48px; height: 48px; display: grid; place-items: center; border-radius: 14px; font-size: 1.35rem; color: var(--ts-text-navy-900); background: var(--ts-bg-amber-350); }
    .ab-icon.is-trial { background: var(--ts-bg-blue-250); }
    .ab-head-text { min-width: 0; }
    .ab-eyebrow { display: block; margin-bottom: .2rem; color: var(--ts-warning-text, #ffd77d); font-size: .7rem; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; }
    .ab-head h2 { margin: 0 0 .35rem; color: var(--ts-text, #eef5ff); font-size: 1.3rem; }
    .ab-head p { margin: 0; max-width: 70ch; color: var(--ts-text-soft, #a9bad0); font-size: .9rem; line-height: 1.55; }

    .ab-body { display: grid; grid-template-columns: minmax(0, 1fr) 300px; gap: 1.25rem; padding: 1.25rem 1.6rem 1.6rem; align-items: start; }
    .ab-main { min-width: 0; display: grid; gap: 1rem; }
    .ab-actions:empty { display: none; }
    .ab-actions { display: flex; flex-wrap: wrap; gap: .6rem; }

    .ab-staff { justify-items: start; padding: 1.4rem; border-radius: 16px; border: 1px solid var(--ts-border, rgba(103,154,236,.2)); background: var(--ts-surface, #0d213c); }
    .ab-staff i { font-size: 1.6rem; color: var(--ts-primary, #2f7cff); }
    .ab-staff h3 { margin: 0; color: var(--ts-text, #eef5ff); font-size: 1.05rem; }
    .ab-staff p { margin: 0; color: var(--ts-text-soft, #a9bad0); font-size: .9rem; line-height: 1.55; }

    .ab-contacts { position: sticky; top: 0; display: grid; gap: .55rem; padding: 1.1rem; border-radius: 16px; border: 1px solid var(--ts-border, rgba(103,154,236,.2)); background: var(--ts-surface, #0d213c); }
    .ab-contacts h3 { display: flex; align-items: center; gap: .45rem; margin: 0; color: var(--ts-text, #eef5ff); font-size: 1rem; }
    .ab-contacts h3 i { color: var(--ts-primary, #2f7cff); }
    .ab-contacts > p { margin: 0 0 .2rem; color: var(--ts-text-soft, #a9bad0); font-size: .82rem; line-height: 1.5; }
    .ab-contact {
      display: flex; align-items: center; gap: .7rem; padding: .65rem .75rem; border-radius: 12px;
      border: 1px solid var(--ts-border, rgba(103,154,236,.2)); background: var(--ts-input-bg, #071a31);
      color: var(--ts-text, #eef5ff); text-decoration: none; font-size: .85rem; font-weight: 600; overflow-wrap: anywhere;
      transition: border-color .2s ease, background-color .2s ease;
    }
    .ab-contact i { flex: 0 0 auto; width: 32px; height: 32px; display: grid; place-items: center; border-radius: 9px; color: var(--ts-primary, #2f7cff); background: var(--ts-bg-blue-400-a12); font-size: 1rem; }
    .ab-contact small { display: block; color: var(--ts-text-muted, #91a4bf); font-size: .7rem; font-weight: 700; letter-spacing: .03em; }
    .ab-contact span { min-width: 0; }
    .ab-contact:hover, .ab-contact:focus-visible { border-color: var(--ts-primary, #2f7cff); outline: none; }
    .ab-contact-primary i { color: var(--ts-text-on-accent); background: var(--ts-bg-green-600); }

    @media (max-width: 900px) {
      .ab-body { grid-template-columns: minmax(0, 1fr); }
      .ab-contacts { position: static; }
    }
    @media (max-width: 600px) {
      .ab-head { flex-direction: column; padding: 1.2rem 1.1rem 1rem; }
      .ab-body { padding: 1rem 1.1rem 1.2rem; }
    }
  `],
})
export class AccessBlockedComponent implements OnInit {
  @Input() mode: 'trial' | 'restricted' = 'restricted';

  private readonly auth = inject(AuthService);
  private readonly contacts = inject(PlatformContactsService);
  readonly mailSubject = encodeURIComponent('Ayuda con el plan de mi negocio en TuSala');

  /** Propietario (legal o delegado): puede renovar/contratar desde su cuenta. Empleados no. */
  readonly canPay = computed(() => this.auth.currentUser()?.role === 'owner');

  readonly supportEmail = computed(() => this.contacts.contacts()?.support_email || 'soporte@tusala.net');
  readonly adminEmail = computed(() => {
    const email = this.contacts.contacts()?.administration_email;
    return email && email !== this.supportEmail() ? email : '';
  });
  private readonly phones = computed(() => (this.contacts.contacts()?.contact_phones || []).filter(Boolean));
  readonly primaryPhone = computed(() => this.phones()[0] || '');
  readonly otherPhones = computed(() => this.phones().slice(1));
  readonly whatsappUrl = computed(() => {
    const digits = this.primaryPhone().replace(/\D/g, '');
    if (!digits) return '';
    const business = this.auth.currentUser()?.company || this.auth.currentUser()?.name || '';
    const text = encodeURIComponent(`Hola, necesito ayuda con el plan de mi negocio${business ? ' (' + business + ')' : ''} en TuSala.`);
    return `https://wa.me/${digits}?text=${text}`;
  });

  ngOnInit(): void {
    // Endpoint público y cacheado (mismo que usa el login).
    this.contacts.load().subscribe({ error: () => {} });
  }
}
